import { Duration } from '../shared/duration.model';
import { ATSettings } from './at-settings.model';
import { BuildProcessStatusId, PostProcessingSettings } from './build-process-status.model';
import * as Steps from './build-process-step.model';
import { ProductionSettings } from './production-settings.model';
import { ExtendedRender } from './render.model';
import { Subscribers } from './subscribers.model';
import { Workers } from './workers.model';

export class BuildProcess extends ExtendedRender implements Duration {
  heartbeatAt: string | null;
  failedAt: string;
  canceledAt: string;
  atSettings: Partial<ATSettings>;
  imagesBucketAddress: string;
  postProcessingSettings: PostProcessingSettings;
  resourceBaseURL: string;
  workers: Workers;
  buildProcessStatusId: number;
  buildProcessTypeId: number;
  runAerotriangulationAutomatically: boolean;
  currentProcessingActivity: string;
  failMessage: string;
  isArchived: boolean;
  progress: number;
  productionSettings: Partial<ProductionSettings>;
  model: {
    id: number;
    name: string;
  };
  modelId: number;
  subscribers: Subscribers;
  startedAt: string;
  finishedAt: string;
  steps: Steps.BuildProcessStepsList;
  withObj: boolean;
  withPointCloud: boolean;
  withOrthoDsm: boolean;
  declaredPixelsAmount: number;
  retryCounter: number;

  constructor(props: Partial<BuildProcess>) {
    super(props);
    this.progress = +this.progress;

    this.steps = {
      upload: new Steps.UploadStep(this),
      at: new Steps.AerotriangulationStep(this),
      reconstruct: new Steps.ReconstructionStep(this),
      prod: new Steps.ProductionStep(this),
      pp: new Steps.PostProductionStep(this),
    };
  }

  isStarted(): boolean {
    return this.buildProcessStatusId === BuildProcessStatusId.Started;
  }

  isFinished(): boolean {
    return this.buildProcessStatusId === BuildProcessStatusId.Finished;
  }

  isFailed(): boolean {
    return this.buildProcessStatusId === BuildProcessStatusId.Failed;
  }

  isCancelled(): boolean {
    return this.buildProcessStatusId === BuildProcessStatusId.Canceled;
  }

  isAccessible(): boolean {
    return !this.isInProgress() && !this.isStarting() && this.steps.upload.allowAccessToProcess();
  }

  getAvailableStep(): number {
    return this.getCurrentStep()?.getAvailableStep() || 0;
  }

  getActiveStep(): number {
    return this.getCurrentStep()?.getActiveStep() || 0;
  }

  getCurrentStep(): Steps.BuildProcessStep | undefined {
    return Object.values(this.steps).find((step: Steps.BuildProcessStep) => step.isCurrent());
  }

  getSubscribers(): { owner: number | undefined; subscribers: number[] } {
    const result = this.subscribers || {};
    const owner = result.owner || undefined;
    const subscribers = result.subscribers || [];
    return { owner, subscribers };
  }

  compareByProgress(another: BuildProcess): number {
    return (
      this.buildProcessTypeId - another.buildProcessTypeId ||
      this.buildProcessStatusId - another.buildProcessStatusId ||
      this.progress - another.progress
    );
  }

  toHttp(): Partial<BuildProcess> {
    const props: Partial<BuildProcess> = { ...this };
    delete props.steps;
    delete props.files;
    return props;
  }

  private noStepIsAvailable(): boolean {
    return this.steps.at.isNotYet();
  }

  private isInProgress(): boolean {
    const progressIsNotFull = this.progress !== 100;
    return this.isStarted() && progressIsNotFull;
  }

  private isStarting(): boolean {
    return this.steps.prod.isStarted() || this.steps.pp.isStarted();
  }
}
